import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from 'prop-types';
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../tailwind.config.js";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, CartesianGrid, Surface, Symbols } from "recharts";
import { formatValue, formatPercentage } from "./Utils.js";
import _ from "underscore";
import moment from "moment";
import { fillColors, fillColorsLight, fillColorsDark } from "../constants.js";
import { useFilterStoreWithDayjsDates } from "../store/filterStore.js";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import "./DropdownMenu.css";
import SmartRechartsTooltip from "./chart-components/SmartRechartsTooltip";

const colors = resolveConfig(tailwindConfig).theme.colors;

const strokeColors = [
	colors.lightblue3,
	colors.teal1,
	colors.yellow1,
	colors.pink3,
	colors.red3,
	colors.lightblue1,
	colors.teal3,
	colors.pink1,
	colors.yellow3,
	colors.red1,
	colors.lightblue1,
];


//return the groupings in the order of the measure, including "Other" if the number of groupings is more than the max groupings when specified
function getGroupings(data, maxGroupings, includeOthers = false, includeForecast = false) {
	var groupings = [];
	var groupedData = _.groupBy(data, "grouping");
	for (const group in groupedData) {
		let groupSum = groupedData[group].reduce((acc, curr) => acc + curr.measure, 0);

		groupings.push({
			grouping: group,
			measure: groupSum,
		});

		if (includeForecast) {
			let groupForecast = groupedData[group].reduce((acc, curr) => acc + (curr.forecast || 0), 0);
			groupings.push({
				grouping: group + " forecast",
				measure: groupForecast,
			});
		}
	}

	groupings.sort((a, b) => b.measure - a.measure);

	let returnValue = groupings.map(item => item.grouping).slice(0, maxGroupings);

	// if the number of groupings is more than the max groupings and other should be included, add "Other" value
	if (includeOthers && returnValue.length >= maxGroupings) {
		returnValue.push("Other");
	}

	return returnValue;
}

function keepOnlyTopGroupings(data, groupings) {
	var result = [];

	if (data && data.length > 0) {
		data.forEach(item => {
			if (groupings.find(group => group === item.grouping)) {
				result.push(item);
			} else {
				result.push({
					...item,
					grouping: "Other"
				})
			}
		});
	}

	return result;
}

function prepareData(data, maxGroupings, dateFormat, includeForecast = false) {

	// check whether the grouping has more than 10 values and order the grouping accordingly
	const groupings = getGroupings(data, maxGroupings, false, includeForecast);	
	const preparedData = keepOnlyTopGroupings(data, groupings);

	var d = _.groupBy(preparedData, "time");

	var result = [];

	for (const [key, value] of Object.entries(d)) {
		var prevData = null;
		var o = {};
		var total = 0;

		for (const item of value) {
			// if the grouping is not in the object, add it with a value of 0
			if (o[item.grouping] === undefined) {
				o[item.grouping] = 0;
				if (includeForecast) {
					o[item.grouping + " forecast"] = 0;
				}
			}

			// add the measure to the grouping
			o[item.grouping] += item.measure || 0;
			if (includeForecast) {
				o[item.grouping + " forecast"] += item.forecast || 0;
			}
			total += (item.measure || 0) + (includeForecast ? (item.forecast || 0) : 0);
		}

		var formattedDate = moment(key).format(dateFormat);

		if (result.length > 0) {
			prevData = Object.fromEntries(
				Object.entries(result[result.length - 1])
					.filter(([key]) => !key.endsWith('_prev'))
			);
		}

		result.push({
			_name: formattedDate,
			_original: key,
			_total: total,
			...o,
			...(prevData ? Object.fromEntries(
				Object.entries(prevData).map(([key, value]) => [`${key}_prev`, value])
			) : {}),
		});
	}

	return result;
}

export const AreaChartComponent_v2 = (props) => {
	const { data, title, dateFormat = "MMM D", legendPosition = "bottom",    stacked,   addFilter,  currentGrouping, height = 340, useOwnBackground = true, includeForecast = false, useV3ForecastStyling = false, maxGroupings: maxGroupingsProp = 10, disableSelection = false, showLegend = true } = props;
	
	// Handle both number and string heights
	const chartHeight = typeof height === 'string' ? height : height;
	const [hoveredArea, setHoveredArea] = useState(null);
	const [lastHoveredArea, setLastHoveredArea] = useState(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
	const [isRightClickContext, setIsRightClickContext] = useState(false);
	const leaveTimeoutRef = React.useRef(null);
	const hoveredAreaRef = React.useRef(null);
	const chartContainerRef = React.useRef(null);
	const [isSelecting, setIsSelecting] = useState(false);
	const [dragStartClientX, setDragStartClientX] = useState(0);
	const [dragCurrentClientX, setDragCurrentClientX] = useState(0);
	const { startDate, endDate, setStartDate, setEndDate } = useFilterStoreWithDayjsDates();
	const pdataRef = React.useRef(null);
	const previousStartRef = React.useRef(null);
	const previousEndRef = React.useRef(null);
	const [showResetButton, setShowResetButton] = useState(false);
	const resetButtonRef = React.useRef(null);

	const handleResetZoom = useCallback((e) => {
		if (e) {
			e.stopPropagation();
		}
		if (previousStartRef.current && previousEndRef.current) {
			setStartDate(previousStartRef.current);
			setEndDate(previousEndRef.current);
			previousStartRef.current = null;
			previousEndRef.current = null;
			setShowResetButton(false);
		}
	}, [setStartDate, setEndDate]);

	const getChartGeometry = useCallback(() => {
		const chartWrapper = chartContainerRef.current?.querySelector('.recharts-wrapper');
		if (!chartWrapper) return null;
		const svg = chartWrapper.querySelector('svg');
		const xAxis = chartWrapper.querySelector('.recharts-xAxis');
		if (!svg || !xAxis) return null;
		const svgRect = svg.getBoundingClientRect();
		const xAxisRect = xAxis.getBoundingClientRect();
		const chartStartX = xAxisRect.left - svgRect.left;
		const chartWidth = xAxisRect.width;
		return { svgRect, chartStartX, chartWidth };
	}, []);

	const clientXToDataIndex = useCallback((clientX) => {
		const pdataLocal = pdataRef.current;
		if (!pdataLocal || pdataLocal.length === 0) return null;
		const geom = getChartGeometry();
		if (!geom || geom.chartWidth === 0) return null;
		const { svgRect, chartStartX, chartWidth } = geom;
		const x = clientX - svgRect.left;
		const relativeX = (x - chartStartX) / chartWidth;
		const clamped = Math.max(0, Math.min(1, relativeX));
		
		// Calculate the exact index position
		// Recharts positions data points evenly: first at 0, last at 1
		const exactIndex = clamped * (pdataLocal.length - 1);
		
		// Find the closest data point index using Math.round
		// This matches how Recharts determines which activeDot to show
		const dataIndex = Math.round(exactIndex);
		
		// Ensure index is within valid range
		return Math.max(0, Math.min(pdataLocal.length - 1, dataIndex));
	}, [getChartGeometry]);

	const handleRightClick = (event) => {
		// This is a fallback for right-clicks on empty areas of the chart
		// Individual areas handle their own right-clicks
		if (event && typeof event.preventDefault === 'function') {
			event.preventDefault();
		}
		// Only show menu if we have a hovered area
		if (hoveredArea || lastHoveredArea) {
			// Toggle menu: close if open, open if closed
			if (menuOpen) {
				setMenuOpen(false);
				setIsRightClickContext(false);
			} else {
				// Use clientX/clientY as fallback if pageX/pageY are not available
				const x = event.pageX || event.clientX || 0;
				const y = event.pageY || event.clientY || 0;
				setMenuPos({ x, y });
				setMenuOpen(true);
				setIsRightClickContext(true);
			}
		}
	};

	const handleMenuOpenChange = (open) => {
		// This is called by Radix UI when the menu state changes
		setMenuOpen(open);
		if (!open) {
			setIsRightClickContext(false);
		}
	};

	const handleClose = () => {
		setMenuOpen(false);
		setIsRightClickContext(false);
	};

	const handleClickOutside = (event) => {
		// Only close if clicking outside the menu, not on right-click release
		if (menuOpen && event.button !== 2) { // button 2 is right mouse button
			setMenuOpen(false);
		}
	};

	// Handle global click events to close menu when clicking outside
	useEffect(() => {
		const handleGlobalClick = (event) => {
			// Only close on left click outside the menu
			if (menuOpen && event.button === 0) { // button 0 is left mouse button
				const menuElement = document.querySelector('[data-radix-popper-content-wrapper]');
				if (menuElement && !menuElement.contains(event.target)) {
					setMenuOpen(false);
					setIsRightClickContext(false);
				}
			}
		};

		if (menuOpen) {
			document.addEventListener('mousedown', handleGlobalClick);
			return () => document.removeEventListener('mousedown', handleGlobalClick);
		}
	}, [menuOpen]);

	const maxGroupings = maxGroupingsProp;
	const pdata = prepareData(data, maxGroupings, dateFormat, includeForecast);
	const sortedHeaders = getGroupings(data, maxGroupings, true, includeForecast);
	const maxValue = Math.max(...pdata.map(item => item._total));

	// Keep pdata in a ref for callbacks defined before it's initialized
	useEffect(() => {
		pdataRef.current = pdata;
	}, [pdata]);

	const normalStopGradient = 0.7;
	const normalStartGradient = 0.7;
	const normalStopOffset = "100%";
	const normalStartOffset = "100%";
	const transparentStopGradient = 1;
	const transparentStartGradient = 0;
	const transparentStopOffset = "30%";
	const transparentStartOffset = "100%";

	const verticalAlign = legendPosition === "right" ? "top" : "bottom";
    const align = legendPosition === "right" ? "right" : "center";
    const layout = legendPosition === "right" ? "vertical" : "horizontal";

	// Create handlers that take header as parameter - these will be called inline in the map
	const handleAreaMouseEnter = useCallback((header) => {
		// Clear any pending leave timeout first
		if (leaveTimeoutRef.current) {
			clearTimeout(leaveTimeoutRef.current);
			leaveTimeoutRef.current = null;
		}
		
		// Always update both ref and state to ensure consistency
		hoveredAreaRef.current = header;
		setHoveredArea(header);
		setLastHoveredArea(header);
	}, []);

	const handleAreaMouseLeave = useCallback((header) => {
		return (e) => {
			// Don't do anything on area mouse leave - let the chart container handle clearing
			// This prevents flickering when moving between stacked areas
			// The chart container's onMouseLeave will handle clearing when truly leaving the chart
			// If moving to another area, its onMouseEnter will update the hover state
		};
	}, []);

	const handleAreaMouseDown = useCallback((header) => {
		return (event) => {
			// Handle right mouse button clicks (button 2)
			if (event.button === 2) {
				// Check if event has preventDefault method before calling it
				if (event && typeof event.preventDefault === 'function') {
					event.preventDefault();
				}
				if (event && typeof event.stopPropagation === 'function') {
					event.stopPropagation();
				}
				// Set the hovered area to this specific area
				setHoveredArea(header);
				setLastHoveredArea(header);
				
				// Toggle menu: close if open, open if closed
				setMenuOpen(prev => {
					if (prev) {
						setIsRightClickContext(false);
						return false;
					} else {
						// Use clientX/clientY as fallback if pageX/pageY are not available
						const x = event?.pageX || event?.clientX || 0;
						const y = event?.pageY || event?.clientY || 0;
						setMenuPos({ x, y });
						setIsRightClickContext(true);
						return true;
					}
				});
			}
		};
	}, []);

	// Handle chart mouse move for selection drag
	const handleChartMouseMove = useCallback((e) => {
		// If selecting, update drag position
		if (isSelecting) {
			setDragCurrentClientX(e.clientX);
		}
	}, [isSelecting]);

	// Handle chart mouse leave to clear hover
	const handleChartMouseLeave = useCallback((e) => {
		// If selecting, ignore mouse leave to allow selection outside container
		if (isSelecting) return;
		// Clear any pending timeouts
		if (leaveTimeoutRef.current) {
			clearTimeout(leaveTimeoutRef.current);
			leaveTimeoutRef.current = null;
		}
		
		// Check if we're moving to another element within the chart container
		const relatedTarget = e.relatedTarget;
		
		// Check if relatedTarget is a valid DOM element (Element or Node)
		if (relatedTarget && relatedTarget.nodeType && typeof relatedTarget.closest === 'function') {
			const chartContainer = chartContainerRef.current;
			const chartWrapper = relatedTarget.closest('.recharts-wrapper');
			
			// Check if we're still within the chart container
			if (chartContainer && typeof chartContainer.contains === 'function' && chartContainer.contains(relatedTarget)) {
				// Still within chart container, don't clear
				return;
			}
			
			if (chartWrapper) {
				// Still within chart wrapper, don't clear
				return;
			}
		}
		
		// We're leaving the chart container - clear hover state
		hoveredAreaRef.current = null;
		setHoveredArea(null);
	}, [isSelecting]);

	// Mouse down on chart container to start selection (left button only)
	const handleChartMouseDown = useCallback((e) => {
		if (disableSelection) return; // Disable selection when in edit mode
		if (e.button !== 0) return; // only left button
		// Ensure we're within the chart area (x axis bounds)
		const geom = getChartGeometry();
		if (!geom) return;
		setIsSelecting(true);
		setDragStartClientX(e.clientX);
		setDragCurrentClientX(e.clientX);
	}, [getChartGeometry, disableSelection]);

	// Finish selection on mouse up
	const handleWindowMouseUp = useCallback((e) => {
		if (!isSelecting) return;
		setIsSelecting(false);
		const startX = dragStartClientX;
		const endX = dragCurrentClientX;
		if (Math.abs(endX - startX) < 4) {
			// Tiny drag -> ignore
			return;
		}
		const fromIdx = clientXToDataIndex(Math.min(startX, endX));
		const toIdx = clientXToDataIndex(Math.max(startX, endX));
		if (fromIdx === null || toIdx === null) return;
		const pdataLocal = pdataRef.current;
		const from = pdataLocal && pdataLocal[fromIdx] ? pdataLocal[fromIdx]._original : null;
		const to = pdataLocal && pdataLocal[toIdx] ? pdataLocal[toIdx]._original : null;
		if (from && to) {
			// Store previous selection for quick reset
			if (startDate && endDate) {
				previousStartRef.current = startDate;
				previousEndRef.current = endDate;
				setShowResetButton(true);
			}
			// Update global period selector via store
			setStartDate(from);
			setEndDate(to);
		}
	}, [isSelecting, dragStartClientX, dragCurrentClientX, clientXToDataIndex, startDate, endDate, setStartDate, setEndDate]);

	// Attach global mouseup when selecting
	useEffect(() => {
		if (!isSelecting) return;
		document.addEventListener('mouseup', handleWindowMouseUp);
		return () => document.removeEventListener('mouseup', handleWindowMouseUp);
	}, [isSelecting, handleWindowMouseUp]);

	// Hide reset button when clicking outside it
	useEffect(() => {
		if (!showResetButton) return;

		const handleClickOutside = (event) => {
			if (resetButtonRef.current && !resetButtonRef.current.contains(event.target)) {
				setShowResetButton(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [showResetButton]);

	// Get gradient fill and stroke based on styling mode
	const getGradientFill = useCallback((idx) => {
		if (useV3ForecastStyling) {
			// Use v3 gradient mapping
			const v3Gradients = [
				"lightblue6", "red8", "yellow4", "pink6", "teal4",
				"lightblue4", "teal6", "pink4", "yellow6", "red4", "lightblue2"
			];
			return `url(#${v3Gradients[idx] || v3Gradients[v3Gradients.length - 1]})`;
		}
		return `url(#gradient-${idx})`;
	}, [useV3ForecastStyling]);

	const getStrokeColor = useCallback((idx) => {
		if (useV3ForecastStyling) {
			return strokeColors[idx] || strokeColors[strokeColors.length - 1];
		}
		return fillColorsDark[idx];
	}, [useV3ForecastStyling]);

	// Update opacity and fill via DOM manipulation to avoid component recreation
	useEffect(() => {
		if (!sortedHeaders || sortedHeaders.length === 0) return;

		const updateAreaStyles = () => {
			// Use both ref and state to determine hover - state is more reliable for updates
			const currentHoveredArea = hoveredAreaRef.current || hoveredArea;
			const isAnyHovered = currentHoveredArea !== null;
			
			// Find all area elements with this dataKey
			// Use chartContainerRef to get the specific chart instance
			const chartWrapper = chartContainerRef.current?.querySelector('.recharts-wrapper') || document.querySelector('.recharts-wrapper');
			if (!chartWrapper) return;
			
			const areas = chartWrapper.querySelectorAll('.recharts-area');
			const areaArray = Array.from(areas);
			
			// Match areas by index - areas should be in the same order as sortedHeaders
			sortedHeaders.forEach((header, idx) => {
				const isHovered = currentHoveredArea === header;
				
				// For v3 styling, keep all areas at full opacity (no transparency on hover)
				// For v2 styling, apply transparency to non-hovered areas
				const opacity = useV3ForecastStyling 
					? 1 
					: (isHovered ? 1 : (isAnyHovered && !isRightClickContext ? 0.3 : 1));
				
				// Get the correct fill URL based on styling mode
				let fillUrl;
				if (useV3ForecastStyling) {
					// For v3 styling, always use the full gradient (no transparent version)
					fillUrl = getGradientFill(idx);
				} else {
					// V2 styling - use transparent gradient when not hovered
					fillUrl = isAnyHovered && !isHovered && !isRightClickContext 
						? `url(#gradient-${idx}-transparent)` 
						: `url(#gradient-${idx})`;
				}
				
				if (areaArray[idx]) {
					const areaElement = areaArray[idx];
					const pathElement = areaElement.querySelector('path');
					if (pathElement) {
						// Set opacity on both the area element and path for better compatibility
						areaElement.style.opacity = opacity;
						pathElement.style.opacity = opacity;
						pathElement.setAttribute('fill', fillUrl);
					}
				}
			});
		};

		// Use requestAnimationFrame to batch DOM updates
		requestAnimationFrame(updateAreaStyles);
	}, [hoveredArea, isRightClickContext, sortedHeaders, useV3ForecastStyling, getGradientFill]);

	// Create activeDot components - need to include hoveredArea to force update
	const bars = useMemo(() => {
		return sortedHeaders.map((header, idx) => {
			// Create activeDot component for this area
			// It will show when this area is hovered (checked via ref and state)
			// Store header and idx in closure
			const activeDotComponent = (props) => {
				// Check if this is the hovered area - check both ref and state for reliability
				const currentHoveredArea = hoveredAreaRef.current || hoveredArea;
				const isHovered = currentHoveredArea === header;
				
				// Only render dot if there's a hovered area AND this is the hovered area
				if (!currentHoveredArea || !isHovered) {
					return null;
				}
				
				// Return the dot element for the hovered area
				return (
					<circle
						cx={props.cx}
						cy={props.cy}
						r={6}
						fill={getStrokeColor(idx)}
						stroke="#fff"
						strokeWidth={2}
					/>
				);
			};
			
			return (
				<Area
					barSize={30}
					isAnimationActive={false}
					type="monotone"
					key={header}
					dataKey={header}
					yAxisId="main"
					stroke={getStrokeColor(idx)}
					strokeWidth={useV3ForecastStyling ? 1 : 2}
					activeDot={activeDotComponent}
					// filter="url(#innerFade)"
					fill={getGradientFill(idx)}
					stackId={stacked ? "a" : idx}
					onMouseEnter={() => handleAreaMouseEnter(header)}
					onMouseLeave={handleAreaMouseLeave(header)}
					onClick={(data, index) => {
						// data: contains info about the area/point clicked on
						// index: the index in your data array
						// Implement logic to mark this area as selected
					  }}
					onMouseDown={handleAreaMouseDown(header)}
				/>
			);
		});
	}, [sortedHeaders, stacked, handleAreaMouseEnter, handleAreaMouseLeave, handleAreaMouseDown, hoveredArea, useV3ForecastStyling, getGradientFill, getStrokeColor]);
	


	function generateNiceTicks(min, max, count) {
		// Add a small buffer (e.g., 5%) to the max value for a nicer axis
		max = max + (max - min) * 0.05;

		// Improved tick generation: handles values < 1 and avoids hard max of 3
		if (count <= 1 || min === max) {
			return [min];
		}
		const range = max - min;
		const rawStep = range / (count - 1);
		// Use a "nice" step size (rounded to nearest power of 10 fraction)
		const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep))));
		const niceStep = Math.max(magnitude / 10, Math.round(rawStep / (magnitude / 10)) * (magnitude / 10));
		const ticks = [];
		let tick = min;
		for (let i = 0; i < count; i++) {
			ticks.push(Number(tick.toFixed(10)));
			tick += niceStep;
		}
		// Ensure last tick is exactly max
		ticks[ticks.length - 1] = max;
		return ticks;
	  }

	// Helper function to get "since..." text based on date format
	const getSinceText = useCallback(() => {
		if (dateFormat === "MMM D") {
			return "since the day before";
		} else if (dateFormat === "YYYY-WW") {
			return "since the week before";
		} else if (dateFormat === "MMM YYYY") {
			return "since the month before";
		} else if (dateFormat === "YYYY-Q") {
			return "since the quarter before";
		} else if (dateFormat === "YYYY") {
			return "since the year before";
		}
		return "";
	}, [dateFormat]);

	// Build tooltip data from Recharts payload - only show hovered series
	const buildAreaChartTooltipData = useCallback((props) => {
		if (!props || !props.payload || props.payload.length === 0) return null;

		// Get the currently hovered area from ref
		const currentHoveredArea = hoveredAreaRef.current;
		if (!currentHoveredArea) return null;

		// Get the first payload item to access the data point
		const firstPayload = props.payload[0];
		const dataPoint = firstPayload.payload;
		
		if (!dataPoint) return null;

		// Extract date/time information
		const original = dataPoint._original || dataPoint._name || props.label || '';
		const total = dataPoint._total || 0;

		// Format date - use "dddd, MMMM D" for day grouping
		const dateMoment = moment(original);
		const formattedDate = dateFormat === "MMM D" 
			? dateMoment.format("dddd, MMMM D") 
			: dateMoment.format(dateFormat);

		// Get value for the hovered area
		const hoveredValue = dataPoint[currentHoveredArea];
		if (hoveredValue === undefined || hoveredValue === null) return null;

		const prevValue = dataPoint[`${currentHoveredArea}_prev`];
		const increase = prevValue ? ((hoveredValue - prevValue) / prevValue) * 100 : 0;

		// Get color based on index
		const idx = sortedHeaders.indexOf(currentHoveredArea);
		let color;
		if (idx >= 0) {
			if (useV3ForecastStyling) {
				color = strokeColors[idx] || strokeColors[strokeColors.length - 1];
			} else {
				color = fillColorsDark[idx];
			}
		} else {
			color = colors.coolgrey3;
		}

		// Create single item for hovered area
		const items = [{
			name: currentHoveredArea,
			value: hoveredValue,
			prevValue: prevValue,
			increase: increase,
			color: color,
			index: 0
		}];

		return {
			label: formattedDate,
			original: original,
			items: items,
			total: total,
			hasMultipleAreas: sortedHeaders && sortedHeaders.length > 1,
			sinceText: getSinceText()
		};
	}, [sortedHeaders, useV3ForecastStyling, dateFormat, getSinceText]);

	// Tooltip content component - matches old CustomTooltipContent style
	const AreaChartTooltipContent = ({ data }) => {
		if (!data || !data.items || data.items.length === 0) return null;

		const { label, items, total, hasMultipleAreas, sinceText } = data;

		return (
			<>
				{/* Date header */}
				<div className="flex flex-col mb-2">
					<div className="flex flex-row">
						<p className="flex-1 label mr-4 text-text-2 font-semibold">
							{label}
						</p>
					</div>
				</div>
				
				{/* Items list */}
				{items.map((item, idx) => {
					const percentageOfTotal = hasMultipleAreas && total > 0 
						? ((item.value / total) * 100) 
						: null;
					const percentageOfTotalText = percentageOfTotal !== null 
						? `${formatPercentage(percentageOfTotal, false)} of total ${formatValue(total, "currency", false)}`  
						: `Total ${formatValue(total, "currency", false)}`;
					const percentageText = Math.abs(item.increase) >= 1 ? formatPercentage(item.increase, true) : "";
					const displayText = percentageText ? `${percentageText} ${sinceText}` : "";
					
					return (
						<div className="value flex flex-col items-start mb-2" key={idx}>
							{/* Series name */}
							<span className="flex-1 mr-4 mb-2 text-text-2">{item.name}</span>

							{/* Value */}
							<span className="w-[150px] text-xl font-semibold mr-3 flex items-baseline text-text-2">
								{formatValue(item.value, "currency", false)}
							</span>

							{/* Percentage change text */}
							{displayText && (
								<span className="mt-2 text-text-2">{displayText}</span>
							)}

							{/* Percentage of total */}
							<span className="text-text-2">{percentageOfTotalText}</span>
						</div>
					);
				})}
			</>
		);
	};

	
	return (
		<div className={`${useOwnBackground ? 'bg-card mt-6 rounded-card border border-card-bd mr-6 p-4 mb-8' : 'flex flex-col h-full flex-1 overflow-hidden bg-card border border-card-bd rounded-card p-4'}`} onMouseDown={handleClickOutside}>
			{pdata && pdata.length > 0 ? (
				<div className="flex-1 min-h-0 relative">
					{useOwnBackground && (
						<div className="text-text text-lg font-semibold mt-1 cursor-default widget-title" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
							{title}
						</div>
					)}
					{!useOwnBackground && (
						<div className="absolute top-0 left-0 z-10 p-2 pointer-events-none">
							<div className="text-text text-lg font-semibold widget-title" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
								{title}
							</div>
						</div>
					)}
					<div className={useOwnBackground ? '' : 'absolute inset-0 top-8'}>
					<div 
						ref={chartContainerRef}
						onContextMenu={handleRightClick}
						onMouseMove={handleChartMouseMove}
						onMouseLeave={handleChartMouseLeave}
						onMouseDown={handleChartMouseDown}
						style={{ position: 'relative', width: '100%', height: '100%' }}
					>
					<ResponsiveContainer width="100%" height={useOwnBackground ? chartHeight : "100%"}>
						<AreaChart
							data={pdata}
							margin={{
								top: 30,
								left: 0,
								bottom: 20,
								right: 40,
							}}
							isAnimationActive={false}
						>
						<defs>
							{useV3ForecastStyling ? (
								<>
									{/* V3 style gradients */}
									<linearGradient id="lightblue6" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.lightblue6} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.lightblue7} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="teal4" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.teal4} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.teal5} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="yellow4" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.yellow4} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.yellow5} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="pink6" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.pink6} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.pink7} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="red8" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.red8} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.red9} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="lightblue4" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.lightblue4} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.lightblue5} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="teal6" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.teal6} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.teal7} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="pink4" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.pink4} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.pink5} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="yellow6" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.yellow6} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.yellow7} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="red4" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.red4} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.red5} stopOpacity={0.3}/>
									</linearGradient>
									<linearGradient id="lightblue2" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor={colors.lightblue2} stopOpacity={0.8}/>
										<stop offset="100%" stopColor={colors.lightblue3} stopOpacity={0.3}/>
									</linearGradient>
									{/* Also include v2 gradients for hover states */}
									{Array.from({ length: 11 }, (_, idx) => (
										<React.Fragment key={`v2-${idx}`}>
											<linearGradient id={`gradient-${idx}-transparent`} x1="0" y1="0" x2="0" y2="1">
												<stop offset={transparentStopOffset} stopColor={fillColors[idx]} stopOpacity={transparentStopGradient}/>
												<stop offset={transparentStartOffset} stopColor={fillColorsLight[idx]} stopOpacity={transparentStartGradient}/>
											</linearGradient>
										</React.Fragment>
									))}
								</>
							) : (
								/* V2 style gradients */
								Array.from({ length: 11 }, (_, idx) => (
									<React.Fragment key={idx}>
										<linearGradient id={`gradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
											<stop offset={normalStopOffset} stopColor={fillColors[idx]} stopOpacity={normalStopGradient}/>
											<stop offset={normalStartOffset} stopColor={fillColorsLight[idx]} stopOpacity={normalStartGradient}/>
										</linearGradient>
										<linearGradient id={`gradient-${idx}-transparent`} x1="0" y1="0" x2="0" y2="1">
											<stop offset={transparentStopOffset} stopColor={fillColors[idx]} stopOpacity={transparentStopGradient}/>
											<stop offset={transparentStartOffset} stopColor={fillColorsLight[idx]} stopOpacity={transparentStartGradient}/>
										</linearGradient>
									</React.Fragment>
								))
							)}
						</defs>

						<CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--chart-grid))" />
						<XAxis dataKey="_name" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" />
						<YAxis yAxisId="main" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" tickFormatter={formatYAxis} domain={[0, 'dataMax']} ticks={generateNiceTicks(0, maxValue, 4)} orientation="left" />
						<YAxis yAxisId="right" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" tickFormatter={formatYAxis} domain={[0, 'dataMax']} ticks={generateNiceTicks(0, maxValue, 4)} orientation="right" />
						<RechartsTooltip
							cursor={false}
							content={SmartRechartsTooltip.createContentCallback({
								buildTooltipData: buildAreaChartTooltipData,
								renderContent: ({ data }) => <AreaChartTooltipContent data={data} />,
								estimatedWidth: 300,
								estimatedHeight: 250,
								offsetX: 50,
								offsetY: -175,
							})}
						/>

						{bars}

						{showLegend && (
							<Legend
								verticalAlign={verticalAlign}
								align={align}
								layout={layout}
								iconType="circle"
								content={renderCustomLegend}
							/>
						)}
					</AreaChart>
					</ResponsiveContainer>
					{/* Drag selection overlay */}
					{isSelecting && (() => {
						const chartWrapper = chartContainerRef.current?.querySelector('.recharts-wrapper');
						const svg = chartWrapper?.querySelector('svg');
						const xAxis = chartWrapper?.querySelector('.recharts-xAxis');
						if (!svg || !xAxis) return null;
						const svgRect = svg.getBoundingClientRect();
						const xAxisRect = xAxis.getBoundingClientRect();
						const chartStartX = xAxisRect.left - svgRect.left;
						const chartWidth = xAxisRect.width;
						const sx = Math.max(svgRect.left + chartStartX, Math.min(svgRect.left + chartStartX + chartWidth, dragStartClientX));
						const cx = Math.max(svgRect.left + chartStartX, Math.min(svgRect.left + chartStartX + chartWidth, dragCurrentClientX));
						const left = Math.min(sx, cx) - svgRect.left;
						const width = Math.abs(cx - sx);
						const overlayHeight = Math.max(0, xAxisRect.top - svgRect.top);
						
						// Calculate start and end dates from drag positions
						const pdataLocal = pdataRef.current;
						let startDateText = '';
						let endDateText = '';
						if (pdataLocal && pdataLocal.length > 0 && chartWidth > 0) {
							const startRelativeX = (Math.min(sx, cx) - (svgRect.left + chartStartX)) / chartWidth;
							const endRelativeX = (Math.max(sx, cx) - (svgRect.left + chartStartX)) / chartWidth;
							const startIdx = Math.max(0, Math.min(pdataLocal.length - 1, Math.round(startRelativeX * (pdataLocal.length - 1))));
							const endIdx = Math.max(0, Math.min(pdataLocal.length - 1, Math.round(endRelativeX * (pdataLocal.length - 1))));
							
							const startDataPoint = pdataLocal[startIdx];
							const endDataPoint = pdataLocal[endIdx];
							
							if (startDataPoint && startDataPoint._original) {
								startDateText = moment(startDataPoint._original).format(dateFormat);
							}
							if (endDataPoint && endDataPoint._original) {
								endDateText = moment(endDataPoint._original).format(dateFormat);
							}
						}
						
						return (
							<>
								<div
									style={{
										position: 'absolute',
										top: 0,
										left: `${left}px`,
										width: `${width}px`,
										height: `${overlayHeight}px`,
										background: 'rgba(100, 149, 237, 0.15)',
										borderLeft: '1px solid rgba(100,149,237,0.5)',
										borderRight: '1px solid rgba(100,149,237,0.5)',
										pointerEvents: 'none',
										zIndex: 5,
									}}
								/>
								{/* Start date label */}
								{startDateText && (
									<div
										style={{
											position: 'absolute',
											top: `${overlayHeight - 20}px`,
											left: `${Math.max(0, left)}px`,
											transform: left < 10 ? 'translateX(0)' : 'translateX(-50%)',
											background: 'rgba(100, 149, 237, 0.9)',
											color: 'white',
											padding: '2px 6px',
											borderRadius: '4px',
											fontSize: '11px',
											whiteSpace: 'nowrap',
											pointerEvents: 'none',
											zIndex: 6,
										}}
									>
										{startDateText}
									</div>
								)}
								{/* End date label - only show if there's enough space from start label */}
								{endDateText && width > 80 && (
									<div
										style={{
											position: 'absolute',
											top: `${overlayHeight - 20}px`,
											left: `${Math.min(svgRect.width - 1, left + width)}px`,
											transform: (left + width) > (svgRect.width - 10) ? 'translateX(-100%)' : 'translateX(-50%)',
											background: 'rgba(100, 149, 237, 0.9)',
											color: 'white',
											padding: '2px 6px',
											borderRadius: '4px',
											fontSize: '11px',
											whiteSpace: 'nowrap',
											pointerEvents: 'none',
											zIndex: 6,
										}}
									>
										{endDateText}
									</div>
								)}
							</>
						);
					})()}

					{/* Reset zoom button */}
					{showResetButton && (
						<button
							ref={resetButtonRef}
							onClick={handleResetZoom}
							className="cursor-pointer absolute top-2 right-2 bg-surface/90 backdrop-blur-md text-text-2 hover:text-text border border-card-bd hover:border-text-3 rounded-ctrl px-3 py-1.5 text-xs shadow-md transition-colors"
							style={{ zIndex: 1000 }}
						>
							Reset zoom
						</button>
					)}
				</div>
				</div>
				</div>
			) : (
				<div className="flex items-center justify-center text-text-2" style={{ height: useOwnBackground ? chartHeight : '100%' }}>
					No data available
				</div>
			)}

			{menuOpen && (
			<DropdownMenu.Root defaultOpen={true} onOpenChange={handleMenuOpenChange}>
				<DropdownMenu.Portal>
					<DropdownMenu.Content
						className="DropdownMenuContent"
						sideOffset={5}
						style={{
						position: "absolute",
						top: menuPos.y-4,
						left: menuPos.x,
						zIndex: 9999,
						width: "auto",
						minWidth: "fit-content",
						whiteSpace: "nowrap",
						maxWidth: "none",
						}}
						onInteractOutside={(event) => {
							// Only close on left click outside, not right click
							if (event.detail.originalEvent.button === 0) {
								handleClose();
							}
						}}
					>
						<DropdownMenu.Item 
							className="DropdownMenuItem"
							style={{ paddingRight: "15px" }}
							onPointerDown={(event) => {
								// Only handle left mouse button clicks (button 0)
								if (event.button === 0) {
									event.preventDefault();
									event.stopPropagation();
									// Add filter for the selected area
									if (addFilter && (hoveredArea || lastHoveredArea) && currentGrouping) {
										const areaName = hoveredArea || lastHoveredArea;
										// Create a filter object for the area
										const filter = {
											category: currentGrouping.value,
											category_display: currentGrouping.label,
											term: areaName
										};
										addFilter(filter);
									}
									handleClose();
								}
							}}
							onPointerUp={(event) => {
								// Prevent any default behavior on pointer up
								event.preventDefault();
								event.stopPropagation();
							}}
							onContextMenu={(event) => {
								// Handle right mouse button clicks
								if (event && typeof event.preventDefault === 'function') {
									event.preventDefault();
								}
								if (event && typeof event.stopPropagation === 'function') {
									event.stopPropagation();
								}
								// Add filter for the selected area
								if (addFilter && (hoveredArea || lastHoveredArea) && currentGrouping) {
									const areaName = hoveredArea || lastHoveredArea;
									// Create a filter object for the area
									const filter = {
										category: currentGrouping.value,
										category_display: currentGrouping.label,
										term: areaName
									};
									addFilter(filter);
								}
								handleClose();
							}}
							onSelect={(event) => {
								// Prevent default selection behavior
								event.preventDefault();
							}}
						>
						Select {hoveredArea || lastHoveredArea}
						</DropdownMenu.Item>
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>
			)}
		</div>
	);
};

function formatYAxis(value) {
	return formatValue(value, "currency", false);
}

const renderCustomLegend = (props) => {
	const { payload } = props;
	return (
	  <ul className="list-none flex p-0 justify-center mt-2">
		{payload.map((entry, index) => (
		  <li key={`item-${index}`} className="flex items-center mr-4">
			{/* Colored Dot */}
			<Surface width={10} height={10} className="mr-2">
			  <Symbols cx={5} cy={5} type="circle" size={75} fill={fillColors[index]} />
			</Surface>
			{/* Series Name */}
			<span className="text-text-2 text-xs">{entry.value}</span>
		  </li>
		))}
	  </ul>
	);
  };

AreaChartComponent_v2.propTypes = {
	data: PropTypes.array,
	title: PropTypes.string,
	dateFormat: PropTypes.string,
	legendPosition: PropTypes.oneOf(['bottom', 'right']),
	dateGrouping: PropTypes.object,
	setDateGrouping: PropTypes.func,
	showButtons: PropTypes.bool,
	stacked: PropTypes.bool,
	filters: PropTypes.array,
	setFilters: PropTypes.func,
	addFilter: PropTypes.func,
	token: PropTypes.string,
	currentGrouping: PropTypes.object,
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	useOwnBackground: PropTypes.bool,
	includeForecast: PropTypes.bool,
	useV3ForecastStyling: PropTypes.bool,
	maxGroupings: PropTypes.number,
	disableSelection: PropTypes.bool,
	showLegend: PropTypes.bool
};
